import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DollarSign, FileText, Briefcase, Layers,
  Activity, ArrowRightLeft, TrendingUpDown,
  BarChart3, Users, ArrowLeft, Building2,
} from 'lucide-react';

import { TRANSLATIONS, translateTerm as translateTermUtil } from './stocks/utils';
import SEO from '../components/SEO';

import OverviewTab        from './stocks/tabs/OverviewTab';
import GovernanceTab      from './stocks/tabs/GovernanceTab';
import ShareholdersTab    from './stocks/tabs/ShareholdersTab';
import FinancialsTab      from './stocks/tabs/FinancialsTab';
import AssetsTab          from './stocks/tabs/AssetsTab';
import TransactionsTab    from './stocks/tabs/TransactionsTab';
import CapitalChangesTab  from './stocks/tabs/CapitalChangesTab';
import DistributionTab    from './stocks/tabs/DistributionTab';
import PeopleTab          from './stocks/tabs/PeopleTab';

export default function CompanyPage() {
  const { cdcvm } = useParams();
  const navigate  = useNavigate();

  // ── App state ───────────────────────────────────────────────────────────────
  const [lang,          setLang]          = useState('pt');
  const [year,          setYear]          = useState(null);
  const [activeTab,     setActiveTab]     = useState('overview');
  const [policyModal,   setPolicyModal]   = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);

  // ── Remote data ─────────────────────────────────────────────────────────────
  const [meta,        setMeta]        = useState(null);   // { nome, cnpj, cd_cvm, years_available }
  const [companyData, setCompanyData] = useState({});     // shape mirrors PETROBRAS_YEARS_DATA[year]
  const [metaLoading, setMetaLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [metaError,   setMetaError]   = useState(null);

  const t            = TRANSLATIONS[lang];
  const translateTerm = useCallback(term => translateTermUtil(term, lang), [lang]);

  // ── Fetch company metadata + available years ────────────────────────────────
  useEffect(() => {
    setMetaLoading(true);
    setMetaError(null);
    axios.get(`/stocks/${cdcvm}`)
      .then(res => {
        setMeta(res.data);
        const years = res.data.years_available || [];
        if (years.length) setYear(Math.max(...years));
      })
      .catch(err => setMetaError(err.response?.data?.error || err.message))
      .finally(() => setMetaLoading(false));
  }, [cdcvm]);

  // ── Fetch all FRE tables for the selected year ──────────────────────────────
  useEffect(() => {
    if (!year) return;
    setDataLoading(true);
    axios.get(`/stocks/${cdcvm}/${year}`)
      .then(res => {
        const d = res.data;
        // OverviewTab accesses petrobrasData[`${year}.csv`] for cia_aberta root info
        if (d.cia_aberta) d[`${year}.csv`] = d.cia_aberta;
        setCompanyData(d);
      })
      .catch(() => setCompanyData({}))
      .finally(() => setDataLoading(false));
  }, [cdcvm, year]);

  // ── Reference date ──────────────────────────────────────────────────────────
  const refDate = useMemo(() => {
    const rootRow = companyData?.cia_aberta?.[0];
    if (rootRow?.DT_REFER) return rootRow.DT_REFER;
    const tableWithDate = Object.values(companyData).find(
      v => Array.isArray(v) && v[0]?.Data_Referencia
    );
    return tableWithDate?.[0]?.Data_Referencia || null;
  }, [companyData]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Loading / error states
  // ─────────────────────────────────────────────────────────────────────────────

  if (metaLoading) {
    return (
      <div className="flex items-center justify-center min-h-64 text-[var(--muted)]">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm font-medium">Carregando empresa...</p>
        </div>
      </div>
    );
  }

  if (metaError) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/companies')}
          className="flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--text)] transition-colors"
        >
          <ArrowLeft size={14} /> Voltar à lista
        </button>
        <div className="bg-red-500/10 border border-red-500/20 text-red-500 rounded-2xl p-6 text-sm">
          <strong>Empresa não encontrada:</strong> {metaError}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Main render
  // ─────────────────────────────────────────────────────────────────────────────

  const years        = (meta?.years_available || []).slice().sort((a, b) => b - a);
  const companyName  = meta?.nome || `CVM ${cdcvm}`;

  return (
    <div className="space-y-6">
      <SEO
        title={`${companyName} - Dados Financeiros (CVM)`}
        description={`Dados financeiros, governança e formulário de referência de ${companyName} na CVM, cobrindo ${years[years.length - 1] || ""} a ${years[0] || ""}.`}
        path={`/companies/${cdcvm}`}
      />

      {/* ── Back link ── */}
      <button
        onClick={() => navigate('/companies')}
        className="flex items-center gap-2 text-xs text-[var(--muted)] hover:text-emerald-500 transition-colors font-semibold"
      >
        <ArrowLeft size={13} />
        Todas as empresas
      </button>

      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-[var(--border)] pb-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mt-0.5">
            <Building2 size={18} className="text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-[var(--text)] leading-tight">
              {companyName}
            </h1>
            <p className="text-xs text-[var(--muted)] mt-1 font-mono">
              CNPJ {meta?.cnpj} · CVM {String(meta?.cd_cvm || '').replace(/^0+/, '')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 self-end sm:self-auto flex-wrap">
          {/* Year selector */}
          {years.length > 0 && (
            <div className="relative">
              <select
                value={year || ''}
                onChange={e => setYear(Number(e.target.value))}
                className="appearance-none bg-[var(--card)] border border-[var(--border)] rounded-xl px-4 py-1.5 pr-8 text-xs font-semibold text-[var(--text)] focus:outline-none focus:border-emerald-500 cursor-pointer shadow-sm"
              >
                {years.map(y => (
                  <option key={y} value={y} className="bg-[var(--card)] text-[var(--text)]">{y}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-[var(--muted)]">
                <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </div>
          )}

          {/* Language toggle */}
          <div className="flex items-center bg-[var(--card)] border border-[var(--border)] p-1 rounded-xl shadow-sm">
            <button
              onClick={() => setLang('pt')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all ${lang === 'pt' ? 'bg-emerald-500 text-white shadow-sm' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}
            >
              Português
            </button>
            <button
              onClick={() => setLang('en')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all ${lang === 'en' ? 'bg-emerald-500 text-white shadow-sm' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}
            >
              English
            </button>
          </div>
        </div>
      </div>

      {/* ── Year data loading indicator ── */}
      {dataLoading && (
        <div className="flex items-center gap-3 text-xs text-[var(--muted)] font-medium py-2">
          <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          Carregando dados de {year}...
        </div>
      )}

      {/* ── Tab Navigation ── */}
      <nav className="sticky top-0 z-10 bg-[var(--bg)]/95 backdrop-blur-md border-b border-[var(--border)] -mx-4 px-4 sm:-mx-6 sm:px-6">
        <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
          {[
            { id: 'overview',       label: t.menu_overview,        icon: FileText       },
            { id: 'distribution',   label: t.menu_distribution,    icon: BarChart3      },
            { id: 'governance',     label: t.menu_governance,      icon: Briefcase      },
            { id: 'shareholders',   label: t.menu_shareholders,    icon: Layers         },
            { id: 'financials',     label: t.menu_financials,      icon: DollarSign     },
            { id: 'assets',         label: t.menu_assets,          icon: Activity       },
            { id: 'transactions',   label: t.menu_transactions,    icon: ArrowRightLeft },
            { id: 'capital_changes',label: t.menu_capital_changes, icon: TrendingUpDown },
            { id: 'people',         label: t.menu_people,          icon: Users          },
          ].map(tab => {
            const Icon     = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                  isActive
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--accent-soft)]'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* ── Tab Contents ── */}
      <div className="transition-all duration-300">

        {activeTab === 'overview'        && <OverviewTab       lang={lang} year={year} petrobrasData={companyData} refDate={refDate} t={t} setPolicyModal={setPolicyModal} />}
        {activeTab === 'governance'      && <GovernanceTab     lang={lang} year={year} petrobrasData={companyData} refDate={refDate} t={t} />}
        {activeTab === 'shareholders'    && <ShareholdersTab   lang={lang} year={year} petrobrasData={companyData} refDate={refDate} t={t} />}
        {activeTab === 'financials'      && <FinancialsTab     lang={lang} year={year} petrobrasData={companyData} refDate={refDate} t={t} />}
        {activeTab === 'assets'          && <AssetsTab         lang={lang} year={year} petrobrasData={companyData} refDate={refDate} t={t} setSelectedAsset={setSelectedAsset} />}
        {activeTab === 'transactions'    && <TransactionsTab   lang={lang} year={year} petrobrasData={companyData} refDate={refDate} t={t} />}
        {activeTab === 'capital_changes' && <CapitalChangesTab lang={lang} year={year} petrobrasData={companyData} refDate={refDate} t={t} />}
        {activeTab === 'distribution'    && <DistributionTab   lang={lang} year={year} petrobrasData={companyData} refDate={refDate} t={t} />}
        {activeTab === 'people'          && <PeopleTab         lang={lang} year={year} petrobrasData={companyData} refDate={refDate} t={t} />}

        {/* ── Trading Policy Modal ── */}
        {policyModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="absolute inset-0" onClick={() => setPolicyModal(null)} />
            <div className="relative bg-[var(--card)] border border-[var(--border)] rounded-2xl max-w-2xl w-full shadow-2xl flex flex-col max-h-[85vh]">
              <div className="flex justify-between items-center border-b border-[var(--border)] px-6 py-4 flex-shrink-0">
                <div>
                  <h3 className="text-base font-bold text-[var(--text)]">
                    {lang === 'pt' ? 'Política de Negociação' : 'Trading Policy'}
                  </h3>
                  <div className="flex gap-4 mt-1 text-xs text-[var(--muted)]">
                    <span>{lang === 'pt' ? 'Aprovada em:' : 'Approved:'} <strong className="text-yellow-500">{(policyModal.Data_Aprovacao || '').split(' ')[0]}</strong></span>
                    <span>{lang === 'pt' ? 'Órgão:' : 'Authority:'} <strong className="text-[var(--text)]">{policyModal.Orgao_Aprovacao}</strong></span>
                  </div>
                </div>
                <button
                  onClick={() => setPolicyModal(null)}
                  className="text-[var(--muted)] hover:text-[var(--text)] text-xs font-semibold p-1.5 hover:bg-[var(--accent-soft)] rounded-lg transition-all flex-shrink-0"
                >
                  {lang === 'pt' ? 'Fechar' : 'Close'}
                </button>
              </div>
              <div className="overflow-y-auto px-6 py-5 space-y-6 text-sm">
                {policyModal.Principais_Caracteristicas && (
                  <div>
                    <h4 className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider mb-3">
                      {lang === 'pt' ? 'Principais Características' : 'Main Characteristics'}
                    </h4>
                    <p className="text-[var(--text)] leading-relaxed whitespace-pre-wrap text-xs">{policyModal.Principais_Caracteristicas}</p>
                  </div>
                )}
                {policyModal.Vedacao_Procedimentos_Fiscalizacao && (
                  <div className="border-t border-[var(--border)] pt-5">
                    <h4 className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider mb-3">
                      {lang === 'pt' ? 'Vedações e Procedimentos de Fiscalização' : 'Restrictions & Enforcement Procedures'}
                    </h4>
                    <p className="text-[var(--text)] leading-relaxed whitespace-pre-wrap text-xs">{policyModal.Vedacao_Procedimentos_Fiscalizacao}</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end px-6 py-4 border-t border-[var(--border)] flex-shrink-0">
                <button
                  onClick={() => setPolicyModal(null)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-5 py-2 rounded-xl shadow-sm transition-all"
                >
                  {lang === 'pt' ? 'Fechar' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Asset Details Modal ── */}
        {selectedAsset && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="absolute inset-0" onClick={() => setSelectedAsset(null)} />
            <div className="relative bg-[var(--card)] border border-[var(--border)] rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 text-sm animate-in zoom-in-95 duration-200 text-[var(--text)]">
              <div className="flex justify-between items-center border-b border-[var(--border)] pb-3">
                <h3 className="text-base font-bold text-[var(--text)]">{t.modal_title}</h3>
                <button
                  onClick={() => setSelectedAsset(null)}
                  className="text-[var(--muted)] hover:text-[var(--text)] text-xs font-semibold p-1 hover:bg-[var(--accent-soft)] rounded-lg transition-all"
                >
                  {t.close}
                </button>
              </div>
              <div className="space-y-3.5 max-h-[70vh] overflow-y-auto pr-1">
                {selectedAsset.assetType === 'intangible' ? (
                  <>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_asset_type}</span>
                      <span className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-600 dark:text-yellow-400 text-[10px] px-2.5 py-0.5 rounded font-bold uppercase tracking-wider inline-block">
                        {translateTerm(selectedAsset.Tipo_Ativo)}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_description}</span>
                      <p className="font-semibold text-[var(--text)] leading-relaxed">{translateTerm(selectedAsset.Descricao_Ativo)}</p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_duration}</span>
                      <p className="font-semibold text-[var(--text)]">{translateTerm(selectedAsset.Duracao)}</p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_loss_event}</span>
                      <p className="font-semibold text-[var(--text)] leading-relaxed">{translateTerm(selectedAsset.Evento_Perda_Direito)}</p>
                    </div>
                    {selectedAsset.Consequencia_Perda_Direito && (
                      <div>
                        <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_loss_consequence}</span>
                        <p className="font-semibold text-[var(--text)] leading-relaxed">{translateTerm(selectedAsset.Consequencia_Perda_Direito)}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_asset_desc}</span>
                      <p className="font-semibold text-[var(--text)] leading-relaxed">{translateTerm(selectedAsset.Descricao_Bem_Ativo)}</p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_location}</span>
                      <p className="font-semibold text-[var(--text)]">
                        {selectedAsset.Municipio || ''} - {selectedAsset.UF || ''}, {translateTerm(selectedAsset.Pais)}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_prop_type}</span>
                      <p className="font-semibold text-[var(--text)]">{translateTerm(selectedAsset.Tipo_Propriedade)}</p>
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setSelectedAsset(null)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-4 py-2 rounded-xl shadow-sm transition-all"
                >
                  {t.close}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
